import { StatusError } from '../components/Providers/ErrorProvider';
import { User } from '../Types/user';

export const API_URL = import.meta.env.VITE_APP_API_HOST;

let setErrorFunction: ((error: StatusError) => Promise<boolean>) | null;
setErrorFunction = null;

export const registerErrorFunction = (fn: (error: StatusError) => Promise<boolean>) => {
  setErrorFunction = fn;
};

export const reportError = async (error: string, status: number) => {
  if (setErrorFunction) {
    return setErrorFunction(new StatusError(error, status));
  } else {
    console.error(error);
  }
};

// Adds auth and timezone headers, and reports connection, 401 and error responses
const authorizedFetch = async (
  url: string,
  options = {} as RequestInit,
  retryTimes = 0
): Promise<Response> => {
  const headers = new Headers(options.headers);
  if (localStorage.getItem('token')) {
    headers.set('Authorization', `Bearer ${localStorage.getItem('token')}`);
  }
  headers.set('Timezone', `${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
  options.headers = headers;

  let response = null;
  try {
    response = await fetch(url, { ...options, credentials: 'include', mode: 'cors' });
  } catch {
    const errorMessage = 'Failed to connect to server';
    reportError(errorMessage, 0);
    throw new StatusError(errorMessage, 0);
  }

  if (response.status === 401) {
    const error = 'Unauthorized';
    const handler = await reportError(error, response.status);
    if (handler && retryTimes < 1) {
      console.log('User session was stale, retrying');
      return authorizedFetch(url, options, 1);
    } else {
      console.log('User refresh failed, logging out');
      throw new Error(error);
    }
  }

  if (response.status < 200 || response.status >= 300) {
    const responseMessage = (await response.json()).message;

    let error = responseMessage ?? 'Failed API Call';
    if (responseMessage.startsWith("This model's maximum context")) {
      error =
        'Sorry, this conversation is too long to process. Until we use a bigger model, please try to use smaller conversations if possible.';
    }
    reportError(error, response.status);
  }
  return response;
};

export const enrichedFetch = async (
  url: string,
  options = {} as RequestInit,
  retryTimes = 0
): Promise<unknown> => {
  const response = await authorizedFetch(url, options, retryTimes);
  if (response.status === 204 || !response.ok) return response;
  return response.json();
};

// Reads a server-sent events response, calling onEvent for each event.
// Returns false if the request failed (the error has already been reported).
export const streamFetch = async (
  url: string,
  options: RequestInit,
  onEvent: (event: string, data: unknown) => void
): Promise<boolean> => {
  const response = await authorizedFetch(url, options);
  if (!response.ok || !response.body) return false;

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;
      let separator;
      while ((separator = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, separator);
        buffer = buffer.slice(separator + 2);
        let event = 'message';
        const dataLines: string[] = [];
        for (const line of rawEvent.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
        }
        if (dataLines.length > 0) onEvent(event, JSON.parse(dataLines.join('\n')));
      }
    }
  } catch {
    // The connection dropped (or sent garbage) after the response started
    reportError('Lost connection to the server while receiving the response', 0);
    return false;
  }
  return true;
};

export const login = (email?: string, password?: string): Promise<User> => {
  const options: RequestInit = {
    method: 'GET',
  };

  if (email && password) {
    options.body = JSON.stringify({
      email,
      password,
    });
    options.method = 'POST';
    options.headers = {
      'Content-Type': 'application/json',
    };
  }

  return enrichedFetch(`${API_URL}/user/login`, options) as Promise<User>;
};

export const loginAzureAD = (): Promise<User> => {
  return enrichedFetch(`${API_URL}/user/login/azuread`) as Promise<User>;
};

export const logoutUser = (): Promise<User> => {
  return enrichedFetch(`${API_URL}/user/logout`, {
    method: 'POST',
  }) as Promise<User>;
};
